import { Gallery, GalleryCell, Viewport, PosterProject } from "PosterEditor/model/poster-project";
import { Size } from "PosterEditor/model/poster-types";

/**
 * Helper to find a viewport by ID within the gallery's nested cell structure.
 * Recursively searches through all cells and nested cells.
 */
export const findViewportInCells = (
  cells: GalleryCell[],
  viewportId: string
): Viewport | undefined => {
  for (const cell of cells) {
    for (const item of cell.items) {
      if (item.type === "viewport" && item.viewport.id === viewportId) {
        return item.viewport;
      } else if (item.type === "cell") {
        const found = findViewportInCells([item.cell], viewportId);
        if (found) return found;
      }
    }
  }
  return undefined;
};

/**
 * Checks if an image element is fully loaded and has valid dimensions.
 * Returns true only if the image is complete and has non-zero natural dimensions.
 */
export const isImageLoaded = (
  imageElement: HTMLImageElement | undefined
): imageElement is HTMLImageElement => {
  return (
    imageElement !== undefined &&
    imageElement.complete &&
    imageElement.naturalWidth > 0 &&
    imageElement.naturalHeight > 0
  );
};

/**
 * Result of finding a viewport with its accumulated size ratios
 */
interface ViewportWithAccumulatedSize {
  viewport: Viewport;
  accumulatedSize: Size; // Product of all parent ratios
}

/**
 * Recursively finds a viewport and calculates its accumulated size ratio.
 * The accumulated size is the product of: gallery size × cell size × viewport size
 */
const findViewportWithAccumulatedSize = (
  cells: GalleryCell[],
  viewportId: string,
  parentAccumulatedSize: Size
): ViewportWithAccumulatedSize | undefined => {
  for (const cell of cells) {
    // Accumulate this cell's size
    const cellAccumulatedSize: Size = {
      width: parentAccumulatedSize.width * cell.size.width,
      height: parentAccumulatedSize.height * cell.size.height,
    };

    for (const item of cell.items) {
      if (item.type === "viewport" && item.viewport.id === viewportId) {
        // Found the viewport - calculate final accumulated size
        return {
          viewport: item.viewport,
          accumulatedSize: {
            width: cellAccumulatedSize.width * item.viewport.size.width,
            height: cellAccumulatedSize.height * item.viewport.size.height,
          },
        };
      } else if (item.type === "cell") {
        const found = findViewportWithAccumulatedSize(
          [item.cell],
          viewportId,
          cellAccumulatedSize
        );
        if (found) return found;
      }
    }
  }
  return undefined;
};

/**
 * Calculate the physical size of a viewport in millimeters.
 *
 * @param posterSizeMM - The poster's physical dimensions in MM
 * @param gallery - The gallery containing the viewport
 * @param viewportId - The ID of the viewport to calculate size for
 * @returns The viewport's physical size in MM, or undefined if not found
 */
export const calculateViewportPhysicalSizeMM = (
  posterSizeMM: { width: number; height: number },
  gallery: Gallery,
  viewportId: string
): { widthMM: number; heightMM: number } | undefined => {
  // Start with gallery size as the initial accumulator
  const gallerySize: Size = {
    width: gallery.size.width,
    height: gallery.size.height,
  };

  const result = findViewportWithAccumulatedSize(
    gallery.content,
    viewportId,
    gallerySize
  );

  if (!result) return undefined;

  return {
    widthMM: posterSizeMM.width * result.accumulatedSize.width,
    heightMM: posterSizeMM.height * result.accumulatedSize.height,
  };
};

/**
 * Calculate the required minimum resolution in pixels for a given physical size.
 *
 * @param widthMM - Physical width in millimeters
 * @param heightMM - Physical height in millimeters
 * @param dpi - Target DPI (default 200)
 * @returns Required resolution in pixels
 */
export const calculateRequiredResolution = (
  widthMM: number,
  heightMM: number,
  dpi: number = 200
): { width: number; height: number } => {
  const mmPerInch = 25.4;
  return {
    width: Math.ceil(widthMM * (dpi / mmPerInch)),
    height: Math.ceil(heightMM * (dpi / mmPerInch)),
  };
};

/**
 * Calculate the maximum resolution to store (allows for zoom/pan headroom).
 * Returns 2x the required resolution, capped at maxPixels.
 *
 * @param requiredWidth - Minimum required width in pixels
 * @param requiredHeight - Minimum required height in pixels
 * @param maxPixels - Maximum dimension cap (default 4000)
 * @returns Maximum dimension to use for resizing
 */
export const calculateMaxResolution = (
  requiredWidth: number,
  requiredHeight: number,
  maxPixels: number = 4000
): number => {
  // 2x the largest required dimension for zoom headroom
  const idealMax = Math.max(requiredWidth, requiredHeight) * 2;
  return Math.min(idealMax, maxPixels);
};

/**
 * Result of image resolution validation
 */
export interface ResolutionValidationResult {
  isValid: boolean;
  reason?: "too_small" | "acceptable" | "too_large";
  requiredWidth: number;
  requiredHeight: number;
  maxResolution: number;
}

/**
 * Validate an image's resolution against the required minimum.
 *
 * @param imageWidth - The image's actual width in pixels
 * @param imageHeight - The image's actual height in pixels
 * @param requiredWidth - The minimum required width
 * @param requiredHeight - The minimum required height
 * @param maxResolution - The maximum resolution to resize to
 * @returns Validation result with reason and required dimensions
 */
export const validateImageResolution = (
  imageWidth: number,
  imageHeight: number,
  requiredWidth: number,
  requiredHeight: number,
  maxResolution: number
): ResolutionValidationResult => {
  const result: ResolutionValidationResult = {
    isValid: true,
    requiredWidth,
    requiredHeight,
    maxResolution,
  };

  // Check if image is too small in either dimension
  if (imageWidth < requiredWidth || imageHeight < requiredHeight) {
    return {
      ...result,
      isValid: false,
      reason: "too_small",
    };
  }

  // Image is large enough
  const largerDimension = Math.max(imageWidth, imageHeight);
  if (largerDimension > maxResolution) {
    return {
      ...result,
      isValid: true,
      reason: "too_large",
    };
  }

  return {
    ...result,
    isValid: true,
    reason: "acceptable",
  };
};

/**
 * Get image dimensions from a File or Blob.
 *
 * @param file - The image file to measure
 * @returns Promise resolving to width and height in pixels
 */
export const getImageDimensions = (
  file: Blob
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };

    img.src = url;
  });
};

/**
 * Collects all viewports from a gallery's nested cell structure.
 * Recursively traverses all cells to find all viewports.
 */
export const getAllViewportsFromCells = (cells: GalleryCell[]): Viewport[] => {
  const viewports: Viewport[] = [];

  for (const cell of cells) {
    for (const item of cell.items) {
      if (item.type === "viewport") {
        viewports.push(item.viewport);
      } else if (item.type === "cell") {
        viewports.push(...getAllViewportsFromCells([item.cell]));
      }
    }
  }

  return viewports;
};

/**
 * Checks if a gallery has any empty viewports (viewports without images).
 * Returns true if the gallery is visible and has at least one viewport with no imageID.
 *
 * @param gallery - The gallery to check, or undefined if no gallery exists
 * @returns true if there are empty viewports that would block rendering
 */
export const hasEmptyViewports = (gallery: Gallery | undefined): boolean => {
  // No gallery or gallery not visible = no empty viewport issue
  if (!gallery || !gallery.isVisible) {
    return false;
  }

  const allViewports = getAllViewportsFromCells(gallery.content);

  // Check if any viewport has no imageID (empty string or undefined)
  return allViewports.some((viewport) => !viewport.imageID);
};
